'use strict';

import { Component } from 'react';
import './NutrientEditor.scss';
import './MacroTotals.scss';
import { smartAdjustMacroPercentages } from '@utils/MacroCalculator';
import ClickableTooltip from '@components/Widgets/ClickableTooltip.react';
import { 
    MACRO_NUTRIENTS, 
    EXPECTED_TOTAL_PERCENTAGE,
    hasMinMax,
    calculateMacroPercentages,
    computePercentageAndSpread,
    formatMacroValue,
    getMacroDisplayName
} from '@utils/MacroNutrientUtils';

export { MACRO_NUTRIENTS }; // Re-export for backward compatibility

export default class MacroTotals extends Component {
    static propTypes = {};

    computePercentAndSpreadFromRange = (nutrNo, range, kcal) => {
        let mode = 'kcal';
        let { per_min = '', per_max = '', g_per_kg_min = '', g_per_kg_max = '' } = range;
        
        // min=0 is valid, don't treat as falsy
        const min = range?.min !== undefined ? range.min : (range?.implicit?.min !== undefined ? range.implicit.min : '');
        const max = range?.max !== undefined ? range.max : (range?.implicit?.max !== undefined ? range.implicit.max : '');
        
        const has = hasMinMax(min, max);

        // Check if we're in g/kg mode
        if (g_per_kg_min || g_per_kg_max) {
            mode = 'g/kg';
            per_min = g_per_kg_min;
            per_max = g_per_kg_max;
        } else if (kcal?.min || kcal?.max) {
            // Calculate percentages for kcal mode
            const percentages = calculateMacroPercentages(
                has.min ? parseFloat(min) : null,
                has.max ? parseFloat(max) : null,
                nutrNo,
                kcal.min,
                kcal.max
            );
            per_min = percentages.perMin;
            per_max = percentages.perMax;
        }

        // Compute percentage and spread, skipping rounding for kcal mode
        // to let smartAdjustMacroPercentages handle it
        const skipRounding = mode === 'kcal';
        const { percentage, spread } = computePercentageAndSpread(
            per_min, 
            per_max, 
            mode,
            skipRounding
        );

        return { 
            mode, 
            min: String(min), 
            max: String(max), 
            per_min, 
            per_max, 
            percentage, 
            spread 
        };
    };

    renderAdjustmentTooltipContent = (pro, fat, cho, adjustmentInfo) => {

        return (
            <div className="macro-adjustment-tooltip">
                <h4>Macro percentages adjusted for rounding accuracy:</h4>

                <div className="macro-details">
                    <div className="macro-row">
                        <span className="macro-name">Fat:</span>
                        <span className="macro-values">
                            {formatMacroValue(fat.originalPercentage, true)}% → {fat.percentage}%
                            {fat.wasAdjusted && <span className="adjusted-indicator"> (adjusted)</span>}
                        </span>
                    </div>
                    <div className="macro-row">
                        <span className="macro-name">Carbohydrates:</span>
                        <span className="macro-values">
                            {formatMacroValue(cho.originalPercentage, true)}% → {cho.percentage}%
                            {cho.wasAdjusted && <span className="adjusted-indicator"> (adjusted)</span>}
                        </span>
                    </div>
                    <div className="macro-row">
                        <span className="macro-name">Protein:</span>
                        <span className="macro-values">
                            {formatMacroValue(pro.originalPercentage, true)}% → {pro.percentage}%
                            {pro.wasAdjusted && <span className="adjusted-indicator"> (adjusted)</span>}
                        </span>
                    </div>
                </div>

                <div className="totals-section">
                    <div className="total-row">
                        <span>Original total:</span>
                        <span>{adjustmentInfo.originalTotal}%</span>
                    </div>
                    <div className="total-row">
                        <span>Adjusted total:</span>
                        <span>{adjustmentInfo.adjustedTotal}%</span>
                    </div>
                </div>

                <p className="explanation">
                    The {getMacroDisplayName(adjustmentInfo.adjustedMacro).toLowerCase()} value was adjusted by{' '}
                    {adjustmentInfo.adjustment > 0 ? '+' : ''}
                    {adjustmentInfo.adjustment}% to ensure the total equals exactly 100%.
                </p>
                {(fat.originalPercentage % 1 !== 0 ||
                    cho.originalPercentage % 1 !== 0 ||
                    pro.originalPercentage % 1 !== 0) && (
                    <p className="explanation" style={{ marginTop: '8px', fontSize: '11px' }}>
                        Note: Exact values are shown above. Switching between KCAL and G/KG modes may cause slight
                        variations due to rounding.
                    </p>
                )}
            </div>
        );
    };

    render() {
        const { envelope } = this.props;

        let pro = this.computePercentAndSpreadFromRange(
                MACRO_NUTRIENTS.PROTEIN,
                envelope[MACRO_NUTRIENTS.PROTEIN],
                envelope[MACRO_NUTRIENTS.CALORIES]
            ),
            fat = this.computePercentAndSpreadFromRange(
                MACRO_NUTRIENTS.FAT,
                envelope[MACRO_NUTRIENTS.FAT],
                envelope[MACRO_NUTRIENTS.CALORIES]
            ),
            cho = this.computePercentAndSpreadFromRange(
                MACRO_NUTRIENTS.CARBS,
                envelope[MACRO_NUTRIENTS.CARBS],
                envelope[MACRO_NUTRIENTS.CALORIES]
            );

        if (pro.mode !== 'kcal' || fat.mode !== 'kcal' || cho.mode !== 'kcal') {
            return <span />;
        }

        // Adjust percentages to ensure they sum to 100%
        const adjusted = smartAdjustMacroPercentages(pro, fat, cho);
        pro = adjusted.protein;
        fat = adjusted.fat;
        cho = adjusted.carbs;
        const adjustmentInfo = adjusted.adjustmentInfo;

        const total = (pro.percentage || 0) + (fat.percentage || 0) + (cho.percentage || 0);
        const wasAdjusted = adjustmentInfo !== null;

        return (
            <div className="nutrient-editor macro-nutrient">
                <div className="col-1">
                    <label></label>
                </div>

                <div className="col-2">
                    <div className="percentage-total entry-container percent-entry" data-mode="total">
                        <input
                            type="number"
                            value={total}
                            ref="input"
                            disabled={true}
                            className="numeric-entry"
                            data-error={false}
                        />
                        <span className="mode-entry-suffix">%</span>
                        <button className="mode-toggle">KCAL</button>
                    </div>
                </div>

                <div className="col-3" />
                <div className="col-4">
                    {total !== EXPECTED_TOTAL_PERCENTAGE ? (
                        <div className="entry-container macro-warning">
                            <i className="icon-warning" /> Macro percentages do not total {EXPECTED_TOTAL_PERCENTAGE}%
                        </div>
                    ) : wasAdjusted ? (
                        <div className="entry-container macro-info">
                            <ClickableTooltip
                                element={
                                    <span className="info-trigger">
                                        <i className="icon-info" /> Values adjusted for rounding
                                    </span>
                                }
                            >
                                {this.renderAdjustmentTooltipContent(pro, fat, cho, adjustmentInfo)}
                            </ClickableTooltip>
                        </div>
                    ) : null}
                </div>
                <div className="col-5" />
            </div>
        );
    }
}
